import { useEffect, useMemo, useState } from 'react';
import type { AnnualWeekdayKey, Grade } from '../types/domain';
import type { DashboardAnnualDaySlot } from '../utils/dashboardAnnualDaySlots';
import {
  presentationSlotsFromSnapshot,
  subscribeDashboardLessonDaySlotsSnapshot,
  type DashboardLessonDaySlotsSnapshotV2,
} from '../utils/dashboardLessonDaySlotsSnapshot';
import { applyProgramContentSlideLearningOverlayToSlots } from '../utils/programContentSlideLearningOverlay';
import {
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT,
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY,
} from '../utils/programContentSlideStorage';
import { useDashboardLessonSectionData } from './useDashboardLessonSectionData';

/**
 * 메인 프레젠테이션 교시 블럭 데이터.
 * 대시보드 데일리와 같은 학기 번들(RQ persist + LS 미러)을 공유한다.
 * - 캐시가 있으면 네트워크 없이 즉시 조립, 날짜 이동은 메모리 번들에서 재조립
 * - 어느 탭에서도 받은 적이 없을 때만 최초 1회 로드(이후 캐시)
 * - 메인 탭 대시보드가 같은 날짜를 다시 계산해 보내면(편집 반영) 그 스냅샷을 우선
 */
export function usePresentationLessonDaySlots(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  dateIso: string;
  enabled: boolean;
}): {
  daySlots: DashboardAnnualDaySlot[];
  loading: boolean;
  /** 로드가 끝났는데도 해당 날짜 시간표를 조립하지 못함 */
  cacheMiss: boolean;
  academicYear: number;
  activeTermNumber: 1 | 2 | null;
} {
  const { workspaceId, gradeId, grades, mondayIso, weekdayKey, dateIso, enabled } = params;
  const wsKey = workspaceId.trim();
  const gidKey = gradeId.trim();
  const dateKey = dateIso.trim();

  const section = useDashboardLessonSectionData({
    workspaceId: wsKey,
    gradeId: gidKey,
    grades,
    mondayIso,
    weekdayKey,
    dateIso: dateKey,
    enabled,
    consumePostLoginRefresh: false,
    publishPresentationSnapshot: false,
  });

  const [liveSnapshot, setLiveSnapshot] = useState<DashboardLessonDaySlotsSnapshotV2 | null>(null);
  const [slideLearningOverlayRev, setSlideLearningOverlayRev] = useState(0);

  useEffect(() => {
    if (!enabled || !wsKey) return undefined;
    return subscribeDashboardLessonDaySlotsSnapshot((snap) => {
      if (snap.workspaceId !== wsKey) return;
      if (!snap.daySlots.length) return;
      setLiveSnapshot(snap);
    });
  }, [enabled, wsKey]);

  useEffect(() => {
    if (!enabled || !wsKey) return undefined;
    const bump = () => setSlideLearningOverlayRev((n) => n + 1);
    const onSameTab: EventListener = () => bump();
    const onStorage = (e: StorageEvent) => {
      if (e.key === PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY) bump();
    };
    window.addEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSameTab);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSameTab);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, wsKey]);

  const liveMatches =
    liveSnapshot != null &&
    liveSnapshot.workspaceId === wsKey &&
    liveSnapshot.gradeId === gidKey &&
    liveSnapshot.dateIso === dateKey &&
    !liveSnapshot.daySlots.some((s) => (s.dateIso ?? '').trim() && s.dateIso.trim() !== dateKey);

  const daySlots = useMemo(() => {
    if (liveMatches && liveSnapshot) return presentationSlotsFromSnapshot(liveSnapshot);
    return applyProgramContentSlideLearningOverlayToSlots(section.daySlots, wsKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMatches, liveSnapshot, section.daySlots, wsKey, slideLearningOverlayRev]);

  const loading = enabled && section.loading && daySlots.length === 0;
  const cacheMiss = enabled && !section.loading && weekdayKey != null && daySlots.length === 0;

  return {
    daySlots,
    loading,
    cacheMiss,
    academicYear: liveMatches && liveSnapshot ? liveSnapshot.academicYear : section.academicYear,
    activeTermNumber:
      liveMatches && liveSnapshot ? liveSnapshot.activeTermNumber : section.activeTermNumber,
  };
}
